/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { Dispatch, SetStateAction } from 'react'
import { ReactComponent as Info } from '@dataplace.ai/assets/lib/icons/largeIcons/info.svg'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { TileFooter } from '@placeme/components/atoms'
import { MainDropdown } from '@dataplace.ai/ui-components/molecules'
import { useIsTrial } from 'apps/placeme/src/customHooks/useIsTrial'
import { SPECIAL_TILE_COSTS } from '@placeme/components/molecules/SectionTile/SectionTile'
import { useSelector } from 'react-redux'
import { RootState } from 'apps/placeme/src/redux/store'
import ChainBenchmarkDescriptionComponent from './components/ChainBenchmarkDescriptionComponent'
import { SelectedLabelProps } from './ChainBenchmarkModel'

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 12rem;
  
  > * {
    cursor: pointer;
  }
`

const DropdownOption = styled.div(({ theme: { typography } }) => css`
  padding: 0.125rem 0.75rem;
  width: 100%;
  justify-content: center;
  display: flex;
  text-align: center;

  :hover {
    font-weight: ${typography.main.pt_15_medium.fontWeight};
  }
`)

const DescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const TrialInfoSpan = styled.span(({ theme }) => css`
  color: ${theme.palette.results.red.darkest};
  font-weight: bold;
`)

interface ChainBenchmarkFormProps {
  handleSubmit: () => void
  handleDeleteTile: () => void
  selectedLabel: SelectedLabelProps | null
  setSelectedLabel: Dispatch<SetStateAction<SelectedLabelProps | null>>
  labelDropdownOptions: SelectedLabelProps[]
}

function ChainBenchmarkForm({
  handleSubmit, handleDeleteTile, selectedLabel, setSelectedLabel, labelDropdownOptions,
}: ChainBenchmarkFormProps) {
  const { t } = useTranslation()
  const { currentSubscriptionData } = useSelector((state: RootState) => state.location)

  const isTrial = useIsTrial()
  const cost = isTrial ? SPECIAL_TILE_COSTS.chain_benchmark_trial : SPECIAL_TILE_COSTS.chain_benchmark

  return (
    <MainContainer>
      <DescriptionContainer>
        <Info
          height={40}
          width={40}
        />
        <ChainBenchmarkDescriptionComponent
          firstDescription='placeme.chain_benchmark.accept_component_description_1'
          secondDescription='placeme.chain_benchmark.accept_component_description_2'
        />
      </DescriptionContainer>
      {isTrial
        ? <TrialInfoSpan>{t('placeme.chain_benchmark.trial_info')}</TrialInfoSpan>
        : null}
      {labelDropdownOptions.length
        ? (
          <MainDropdown
            content={(
              <DropdownWrapper>
                {labelDropdownOptions.map(({
                  id, value,
                }) => (
                  <DropdownOption
                    key={value}
                    aria-hidden='true'
                    onClick={() => setSelectedLabel({
                      id,
                      value,
                    })}
                  >
                    {value}
                  </DropdownOption>
                ))}
              </DropdownWrapper>
            )}
            header={selectedLabel?.value ?? t('generic.choose')}
            width='6rem'
          />
        )
        : t('placeme.chain_benchmark.no_labels_info')}

      <TileFooter
        isUnlimited={currentSubscriptionData?.value?.planExact?.includes('unlimited') || currentSubscriptionData?.value?.plan === 'white'}
        label={t('generic.apply')}
        onAccept={handleSubmit}
        onCancel={handleDeleteTile}
        value={cost}
      />
    </MainContainer>
  )
}

export default ChainBenchmarkForm
